import { css } from '@styled-system/css';
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import type { Plugin } from 'chart.js';
import { type FC } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { Message } from '@/shared/components/ui/Message';
import { Spinner } from '@/shared/components/ui/Spinner';
import { dsTokens } from '@/shared/theme/tokens';
import { formatPrice } from '@/shared/utils/format';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// ============================================================================
// Répartition des prix en barres (buckets) avec marqueurs verticaux sur les
// quartiles : « Achat » (Q1) et « Revente » (Q3).
// ============================================================================

interface PriceDistributionBarsProps {
  buckets: { price: number; count: number }[] | null;
  priceMin: number | null;
  q1: number | null;
  q3: number | null;
  loading?: boolean;
}

/** Agrège les buckets en MAX_BARS groupes (somme des counts, borne haute du groupe). */
const MAX_BARS = 6;

const aggregateBuckets = (
  buckets: { price: number; count: number }[],
): { price: number; count: number }[] => {
  if (buckets.length <= MAX_BARS) return buckets;
  const groupSize = Math.ceil(buckets.length / MAX_BARS);
  const result: { price: number; count: number }[] = [];
  for (let i = 0; i < buckets.length; i += groupSize) {
    const group = buckets.slice(i, i + groupSize);
    result.push({
      price: group[group.length - 1].price,
      count: group.reduce((sum, b) => sum + b.count, 0),
    });
  }
  return result;
};

const containerStyles = css({
  width: '100%',
  position: 'relative',
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '280px',
});

/** Pixel X d'un prix sur un axe catégoriel de buckets (interpolation linéaire). */
const pixelForPrice = (
  chart: ChartJS,
  buckets: { price: number; count: number }[],
  priceMin: number,
  price: number,
): number | null => {
  const scale = chart.scales.x;
  if (!scale) return null;

  // Bornes de chaque bucket : [borne basse, prix du bucket]
  let lower = priceMin;
  for (let i = 0; i < buckets.length; i += 1) {
    const upper = buckets[i].price;
    if (price <= upper || i === buckets.length - 1) {
      const ratio = upper === lower ? 0.5 : Math.min(Math.max((price - lower) / (upper - lower), 0), 1);
      const center = scale.getPixelForValue(i);
      const halfWidth =
        buckets.length > 1
          ? Math.abs(scale.getPixelForValue(Math.min(i + 1, buckets.length - 1)) - scale.getPixelForValue(Math.max(i - 1, 0))) /
            (i === 0 || i === buckets.length - 1 ? 2 : 4) /
            1
          : 40;
      return center - halfWidth + ratio * halfWidth * 2;
    }
    lower = upper;
  }
  return null;
};

export const PriceDistributionBars: FC<PriceDistributionBarsProps> = ({ buckets, priceMin, q1, q3, loading }) => {
  const { t } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];

  if (loading) {
    return (
      <div className={centered}>
        <Spinner size={24} />
      </div>
    );
  }

  if (!buckets || buckets.length === 0) {
    return (
      <Message type="info" bordered>
        {t('statsPanel.noDistribution')}
      </Message>
    );
  }

  const bars = aggregateBuckets(buckets);

  const barColor = ds['brand.primary'];
  const buyColor = ds['warning.default'];
  const sellColor = ds['success.default'];
  const labelColor = ds['text.secondary'];

  // Marqueurs verticaux Achat (Q1) / Revente (Q3) dessinés par un plugin inline.
  const markersPlugin: Plugin<'bar'> = {
    id: 'quartileMarkers',
    afterDatasetsDraw: (chart) => {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;

      const markers: { price: number | null; label: string; color: string; dashed: boolean }[] = [
        { price: q1, label: t('statsPanel.markerBuy'), color: buyColor, dashed: true },
        { price: q3, label: t('statsPanel.markerSell'), color: sellColor, dashed: false },
      ];

      markers.forEach((marker) => {
        if (marker.price == null) return;
        const x = pixelForPrice(chart, bars, priceMin ?? 0, marker.price);
        if (x == null || x < chartArea.left || x > chartArea.right) return;

        ctx.save();
        ctx.strokeStyle = marker.color;
        ctx.lineWidth = 2;
        if (marker.dashed) ctx.setLineDash([5, 4]);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top + 14);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = marker.color;
        ctx.font = '600 11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(marker.label, x, chartArea.top + 8);
        ctx.restore();
      });
    },
  };

  const chartData: ChartData<'bar'> = {
    labels: bars.map((bucket) => formatPrice(bucket.price)),
    datasets: [
      {
        data: bars.map((bucket) => bucket.count),
        backgroundColor: `${barColor}B3`, // ~70% alpha
        hoverBackgroundColor: barColor,
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.85,
        categoryPercentage: 0.9,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: ds['surface.elevated'],
        titleColor: ds['text.primary'],
        bodyColor: ds['text.secondary'],
        borderColor: ds['border.default'],
        borderWidth: 1,
        padding: 8,
        displayColors: false,
        callbacks: {
          title: (items: TooltipItem<'bar'>[]) => {
            const index = items[0].dataIndex;
            const maxPrice = bars[index].price;
            const minPrice = index === 0 ? (priceMin ?? 0) : bars[index - 1].price;
            return `${formatPrice(minPrice)} – ${formatPrice(maxPrice)}`;
          },
          label: (item: TooltipItem<'bar'>) => t('statsPanel.tooltipSold', { count: bars[item.dataIndex].count }),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: ds['text.tertiary'], font: { size: 11 } },
        border: { color: ds['border.default'] },
      },
      y: {
        beginAtZero: true,
        grid: { color: `${ds['border.subtle']}80` },
        ticks: { color: ds['text.tertiary'], font: { size: 11 }, precision: 0 },
        border: { display: false },
      },
    },
  };

  return (
    <div className={containerStyles} style={{ height: 280, color: labelColor }}>
      <Bar data={chartData} options={options} plugins={[markersPlugin]} />
    </div>
  );
};
